import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  debounce,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { ApiStatistics } from '../../../api/hexa/ApiStatistics';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { LocalizationProvider, DateRangePicker } from '@mui/x-date-pickers-pro';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import ReactApexChart from 'react-apexcharts';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { ApexOptions } from 'apexcharts';
import { LoadingButton } from '@mui/lab';
import { StatisticBar } from '../../../api/hexa/models/StatisticsModels';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FilterSelectorComponent from '../../components/FilterSelector';
import AutocompleteFilterComponent from '../../components/AutocompleteFilter';
import CustomerStatisticsRequests from './CustomerStatisticsRequests';

interface DateRangeInterface {
  start_date: string | undefined;
  end_date: string | undefined;
}

interface prova {
  minDate: Dayjs | null;
  maxDate: Dayjs | null;
}

const CustomerStatistics = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<string>('');
  const [datePickerError, setDatePickerError] = useState<string>('');
  const [chartData, setChartData] = useState<[number, number][]>([]);
  const [requestOptions, setRequestOptions] = useState<string[]>([]);
  const [methodOptions, setMethodOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState<string[]>([
    '200',
  ]);
  const [selectedMethods, setSelectedMethods] = useState<string[]>(['GET']);
  const [datesBoundaries, setDateBoundaries] = useState<prova>({
    minDate: null,
    maxDate: null,
  });
  const [snackbarError, setSnackbarError] = useState({
    open: false,
    message: '',
  });
  const [selectedDates, setSelectedDates] = useState<DateRangeInterface>({
    start_date: undefined,
    end_date: undefined,
  });
  const isLoadingRef = useRef(isLoading);
  const chartConfig = {
    chart: {
      id: 'requestsChart',
      type: 'area',
    },
    series: [{ name: 'Requests per day', data: chartData }],
    options: {
      chart: {
        id: 'area-datetime',
        zoom: {
          autoScaleYaxis: true,
        },
        type: 'area',
        height: 350,
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: false,
            selection: false,
            customIcons: [
              {
                icon: `
                  <div class="chart-icon">                
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" viewBox="0 0 512 437.242" fill="currentColor">
                      ${downloadIconPath}
                    </svg>
                    <span class="chart-icon-label">Download raw data </span>
                  </div>
                `,
                index: 0,
                title: t('customer_statistics.download'),
                class: 'custom-icon',
                click: () => downloadStatistics(),
              },
            ],
          },
        },
      },
      noData: {
        text: t('customer_statistics.no_data'),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: -10,
        style: {
          color: sgrColors.backdrop,
          fontSize: '1.5rem',
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '0.75rem',
          colors: [sgrColors.azure],
        },
        background: {
          enabled: true,
          borderRadius: 2,
          borderWidth: 0,
          borderColor: sgrColors.azure,
          opacity: 0.8,
        },
      },
      markers: {
        size: 0,
        style: 'hollow',
      },
    },
  };

  const getChartData = useCallback(
    (
      statistics: StatisticBar[],
      filterStartDate: string,
      filterEndDate: string
    ) => {
      // Get the lower and upper date boundaries
      let completeData: [number, number][] = [];
      let currentDate = dayjs(filterStartDate);
      let lastDate = dayjs(filterEndDate);
      /**
       * Create a map of the dates so we can easily check if a date is present
       * and if it is not, add it to the data array with a 0 value
       */
      let dataMap = new Map(
        statistics.map((d) => [
          dayjs(d.created_date).format('DD-MM-YYYY'),
          d.count,
        ])
      );
      while (
        currentDate.isBefore(lastDate) ||
        currentDate.isSame(lastDate, 'day')
      ) {
        const dateMapKeyString = currentDate.format('DD-MM-YYYY');
        completeData.push([
          currentDate.valueOf(), // Convert to timestamp as requested by the chart
          dataMap.get(dateMapKeyString) || 0, // Fill missing dates with 0
        ]);
        currentDate = currentDate.add(1, 'day'); // go to the next day
      }
      setChartData(completeData);
    },
    []
  );

  /**
   * The the contract end is after today, use today instead. If the contract start date is
   * more than 6 months before the contract end date use the contract end date minus
   * 6 months as the start date, otherwise just use the contract start date
   */
  const getMinAndMaxDates = useCallback(
    (contractStartDate: dayjs.Dayjs, contractEndDate: dayjs.Dayjs) => {
      let filterStartDate;
      if (contractEndDate.isAfter(dayjs(), 'day')) {
        contractEndDate = dayjs();
      }
      if (contractStartDate.isBefore(contractEndDate.subtract(6, 'months'))) {
        filterStartDate = contractEndDate.subtract(6, 'month');
      } else {
        filterStartDate = contractStartDate;
      }
      setDateBoundaries({
        minDate: filterStartDate,
        maxDate: contractEndDate,
      });
    },
    []
  );

  /**
   * Fetch statistics from the API and set the chart state
   */
  const fetchStatistics = useCallback(async () => {
    if (!customerId || !datesBoundaries.minDate || !datesBoundaries.maxDate)
      return;
    try {
      if (!isLoadingRef.current) setIsLoading(true);
      const startDate =
        selectedDates.start_date ?? datesBoundaries.minDate?.toISOString();
      const endDate =
        selectedDates.end_date ?? datesBoundaries.maxDate.toISOString();
      const { total, statistics } = await ApiStatistics.index(customerId, {
        url_names: selectedRequests,
        start_date: startDate,
        end_date: endDate,
        methods: selectedMethods,
        status_codes: selectedStatusCodes.map((code) => parseInt(code)),
      });
      if (statistics.length > 0) {
        getChartData(statistics, startDate, endDate);
      } else {
        setChartData([]);
      }
      setTotal(
        new Intl.NumberFormat('en-gb', { minimumFractionDigits: 0 }).format(
          total
        )
      );
    } catch (error: any) {
      setSnackbarError({
        open: true,
        message: `Error ${error.status ?? 500} fetching statistics: 
          ${error.response?.data.error ?? 'Error fetching statistics'}`,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    customerId,
    datesBoundaries.maxDate,
    datesBoundaries.minDate,
    getChartData,
    selectedDates.end_date,
    selectedDates.start_date,
    selectedMethods,
    selectedRequests,
    selectedStatusCodes,
  ]);

  /**
   * Fetch the filters data from the API, set the selector options
   * and the date boundaries
   * !! isLoading is not set to false here because once the filters are
   * fetched the statistics retrieve is triggered, so it is set to false
   * after everything is ready at the end of fetchStatistics function
   */
  const fetchFiltersData = useCallback(async () => {
    if (!customerId) return;
    try {
      setIsLoading(true);
      const {
        url_names,
        methods,
        status_codes,
        contract_start_date,
        contract_end_date,
      } = await ApiStatistics.get_filters_data(customerId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      getMinAndMaxDates(dayjs(contract_start_date), dayjs(contract_end_date));
      setRequestOptions(url_names);
      setMethodOptions(methods);
      setStatusOptions(status_codes);
    } catch (error: any) {
      setSnackbarError({
        open: true,
        message: `Error ${error.status ?? 500} fetching filters data: 
            ${error.response?.data.error ?? 'Error fetching filters data'}`,
      });
      setIsLoading(false);
    }
  }, [customerId, getMinAndMaxDates]);

  useEffect(() => {
    fetchFiltersData();
  }, [fetchFiltersData]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const downloadStatistics = async () => {
    if (customerId) {
      setIsLoading(true);
      try {
        const startDate =
          selectedDates.start_date ?? datesBoundaries.minDate?.toISOString();
        const endDate =
          selectedDates.end_date ?? datesBoundaries.maxDate?.toISOString();
        const data = await ApiStatistics.download_data(customerId, {
          url_names: selectedRequests,
          start_date: startDate,
          end_date: endDate,
          methods: selectedMethods,
          status_codes: selectedStatusCodes.map((code) => parseInt(code)),
        });
        const blobStream = data.stream();
        const reader = blobStream.getReader();
        const chunks = [];
        let done = false;
        // Read the stream until completion
        while (!done) {
          const { value, done: readerDone } = await reader.read();
          if (value) {
            chunks.push(value);
          }
          done = readerDone;
        }
        // Combine the chunks into a Blob
        const blob = new Blob(chunks, { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'statistics.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        setSnackbarError({
          open: true,
          message: `Error ${error.status ?? 500} downloading statistics: 
            ${error.response?.data.error ?? 'Error downloading statistics'}`,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarError({
      open: false,
      message: snackbarError.message,
    });
  };

  const applyEntitiesFilter = () => {
    setSelectedMethods(['GET']);
    setSelectedRequests(['entities.index']);
    setSelectedStatusCodes(['200']);
  };

  /**
   * Debounce the setDates function to avoid multiple calls and peform
   * the date validation, needed when the user writes down the dates
   * in the input
   */
  const debouncedSetDates = useMemo(
    () =>
      debounce((value: [Dayjs | null, Dayjs | null]) => {
        try {
          if (!value[0] || !value[1]) return;
          const diffInYears = value[1].diff(value[0], 'year', true);
          const isBefore = value[1].isBefore(value[0], 'day');
          if (diffInYears > 1) {
            setDatePickerError(
              t('customer_statistics.date_picker_range_error')
            );
            return;
          } else if (isBefore) {
            setDatePickerError(t('customer_statistics.date_picker_end_error'));
            return;
          } else {
            setDatePickerError('');
          }
          const newDates: DateRangeInterface = {
            start_date: value[0]?.toDate().toISOString(),
            end_date: value[1]?.toDate().toISOString(),
          };
          setSelectedDates(newDates);
        } catch (error) {
          setDatePickerError(t('customer_statistics.date_picker_error'));
        }
      }, 500),
    [t]
  );

  const onDatePickerChange = useCallback(
    (value: [Dayjs | null, Dayjs | null]) => {
      debouncedSetDates(value);
    },
    [debouncedSetDates]
  );

  useEffect(() => {
    isLoadingRef.current = isLoading;
    console.log(isLoadingRef.current);
  }, [isLoading]);

  return (
    <>
      <Container>
        <Card
          component="form"
          sx={{
            borderRadius: '0.5rem !important',
            paddingX: '1rem',
          }}
        >
          <CardHeader
            title={
              <Typography variant="h3">
                {t('customer_statistics.title')}
              </Typography>
            }
          />
          <CardContent sx={{ paddingTop: '0 !important' }}>
            <Box
              sx={{
                width: { xs: '100%', md: '60%' },
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6" sx={{ paddingLeft: '1rem' }}>
                <b>{t('customer_statistics.total_requests')}</b> {total}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: '2rem',
                justifyContent: 'space-evenly',
              }}
            >
              <Box sx={{ width: { xs: '100%', md: '65%' } }}>
                <div style={{ position: 'relative' }}>
                  {isLoading && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={'1.5rem'} />
                      <Box sx={{ paddingLeft: '1rem' }}>
                        {t('customer_statistics.loading')}
                      </Box>
                    </div>
                  )}
                  <ReactApexChart
                    height={365}
                    chart={chartConfig.chart}
                    series={chartConfig.series}
                    options={
                      {
                        ...chartConfig.options,
                        xaxis: {
                          type: 'datetime',
                          tickPlacement: 'on',
                          labels: {
                            formatter: function (value) {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-GB');
                            },
                          },
                        },
                        dataLabels: {
                          enabled: false,
                          formatter: (value: number) => {
                            if (value === 0) return '';
                            return `${value}`;
                          },
                          background: {
                            opacity: 0.8,
                          },
                        },
                      } as ApexOptions
                    }
                    type="area"
                  ></ReactApexChart>
                </div>
                <LoadingButton
                  loading={isLoading}
                  onClick={() => applyEntitiesFilter()}
                  color="primary"
                  aria-label="quick filter"
                  variant="text"
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    paddingLeft: '1rem',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      fontColor: sgrColors.azure,
                      color: sgrColors.azure,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {t('customer_statistics.quick_filter')}
                </LoadingButton>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  width: { xs: '100%', md: '35%' },
                }}
              >
                <AutocompleteFilterComponent
                  label="customer_statistics.request_selector"
                  options={requestOptions}
                  selectedOptions={selectedRequests}
                  setSelectedOptions={setSelectedRequests}
                />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', marginBottom: '-0.5rem' }}
                >
                  {t('customer_statistics.date_picker_label')}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateRangePicker
                    slots={{ field: SingleInputDateRangeField }}
                    value={[datesBoundaries.minDate, datesBoundaries.maxDate]}
                    format="DD/MM/YYYY"
                    onChange={(value) => {
                      onDatePickerChange(value);
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                      },
                      textField: {
                        error: datePickerError !== '',
                        helperText: datePickerError || '',
                      },
                    }}
                    disableAutoMonthSwitching
                    localeText={{
                      start: t('customer_statistics.date_picker_start'),
                      end: t('customer_statistics.date_picker_end'),
                    }}
                    shouldDisableDate={(date: Dayjs) => {
                      const maxEndDate = dayjs(selectedDates.start_date).add(
                        1,
                        'year'
                      );
                      if (
                        datesBoundaries &&
                        date.isAfter(dayjs(datesBoundaries?.maxDate), 'day')
                      ) {
                        return true;
                      }
                      return date.isAfter(maxEndDate);
                    }}
                  />
                </LocalizationProvider>
                <FilterSelectorComponent
                  label="customer_statistics.method_selector"
                  options={methodOptions}
                  selectedOptions={selectedMethods}
                  setSelectedOptions={setSelectedMethods}
                />
                <FilterSelectorComponent
                  label="customer_statistics.status_selector"
                  options={statusOptions}
                  selectedOptions={selectedStatusCodes}
                  setSelectedOptions={setSelectedStatusCodes}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
        {customerId &&
          (selectedDates.start_date ?? datesBoundaries.minDate) &&
          (selectedDates.end_date ?? datesBoundaries.maxDate) && (
            <CustomerStatisticsRequests
              customerId={customerId}
              startDate={
                selectedDates.start_date ??
                (datesBoundaries.minDate as Dayjs).toISOString()
              }
              endDate={
                selectedDates.end_date ??
                (datesBoundaries.maxDate as Dayjs).toISOString()
              }
              urlNames={selectedRequests}
              methods={selectedMethods}
              statusCodes={selectedStatusCodes.map((code) => parseInt(code))}
            />
          )}
        <Outlet />
      </Container>
      <Snackbar
        open={snackbarError.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {snackbarError.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomerStatistics;

const downloadIconPath = ` <path fill="currentColor" fill-rule="nonzero" d="M.723 313.756c-2.482-10.26 1.698-18.299 8.38-23.044a23.417 
                      23.417 0 018.018-3.632c2.877-.7 5.88-.865 8.764-.452 8.127 1.166 15.534 6.417 18.013 16.677a632.525 632.525 0 
                      014.317 19.091c1.566 7.418 2.52 12.234 3.418 16.772 4.445 22.443 7.732 36.512 16.021 43.526 8.775 7.423 25.366 
                      9.985 57.167 9.985h268.042c29.359 0 44.674-2.807 52.736-10.093 7.768-7.023 10.805-20.735 14.735-41.777l.007-.043a1038.93 1038.93 
                      0 013.426-17.758c1.298-6.427 2.722-13.029 4.34-19.703 2.484-10.256 9.886-15.503 18.008-16.677 2.861-.41 5.846-.242 8.722.449 2.905.699 
                      5.679 1.935 8.068 3.633 6.672 4.741 10.843 12.762 8.38 22.997l-.011.044a494.136 494.136 0 00-3.958 17.974c-1.011 5.023-2.169 11.215-3.281 
                      17.178l-.008.043c-5.792 31.052-10.544 52.357-26.462 67.319-15.681 14.741-40.245 20.977-84.699 20.977H124.823c-46.477 
                      0-72.016-5.596-88.445-20.144-16.834-14.909-21.937-36.555-28.444-69.403-1.316-6.654-2.582-13.005-3.444-17.126-1.213-5.781-2.461-11.434-3.767-16.813zm165.549-143.439l65.092 
                      68.466.204-160.91h47.595l-.204 160.791 66.774-70.174 34.53 32.848-125.184 131.556-123.336-129.729 34.529-32.848zm65.325-115.413l.028-22.041h47.594l-.028 
                      22.041h-47.594zm.046-36.254L231.666 0h47.595l-.024 18.65h-47.594z"/>`;
